/* =========================================================================
 * COLLEGE — national and association championships.
 *
 * Brian: "we now have to do it with colleges. Conference and national at every
 * level."
 *
 * ⚠️ THIS IS A RECONCILIATION, NOT A GREEN FIELD. The object already holds
 * 3,126 college-level records — 2,716 conference, 371 national, across 179
 * conferences and 50 sports. Sized before any research was commissioned,
 * because Nevada taught that "we hold none of this" is usually wrong: a browser
 * pass there established 92 titles and 78 were already present. The dedupe
 * check decides what is actually new.
 *
 * Field values match what the object already uses for each kind, rather than
 * what would read best. `sr_segment` is an ENUM and an invented value loses the
 * whole record.
 * ========================================================================= */

const C = (body, level, segment, sport, gender, division, source) => ({
  st: 'US', state: '', body, sport, gender,
  divisions: [division],
  source, segment, level,
  season: '2026', activity: false, note: '',
})

/* ---- NCAA — 91 team championships across 24 sports ---------------------
 * Enumerated off the NCAA's own championship pages, one per crowned TEAM
 * title. The NCAA's marketing copy says "92 championships"; the enumeration
 * came to 91 and the discrepancy is REPORTED rather than closed by inventing a
 * 92nd. An unsourced marketing number is not a list.
 *
 * ⚠️ THE NCAA DOES NOT CROWN FBS FOOTBALL. The College Football Playoff is a
 * separate entity. NCAA football is FCS, DII and DIII — three titles. Adding a
 * "DI Football" record would invent the most famous title in college sport.
 *
 * ⚠️ NATIONAL COLLEGIATE IS ONE CHAMPIONSHIP, NOT THREE. Twelve sports are
 * contested as a single all-division championship because too few schools
 * sponsor them to fill three brackets — beach volleyball, bowling, fencing,
 * both gymnastics, women's ice hockey, rifle, skiing, men's volleyball, both
 * water polo, women's wrestling. Recording those per division would triple
 * them. (DIII men's volleyball and DIII women's ice hockey DO have their own
 * championships and are listed separately — the National Collegiate event in
 * those two is DI/DII only.)
 *
 * ⚠️ TEAM TITLES ONLY. Cross country, track, swimming, wrestling, golf,
 * tennis, gymnastics, fencing, skiing and rifle also crown individual and
 * event champions — real, but a person rather than a school, and a separate
 * decision. Letting a results page of 20 event winners become 20 championships
 * is how a count triples.
 *
 * ⚠️ APPROVED BUT NOT YET HELD, so absent: DIII women's wrestling and DII
 * women's bowling (inaugural 2028), acrobatics & tumbling and STUNT
 * (inaugural 2027). A title a year early is a rep working a championship that
 * does not exist yet. Flag football is an Emerging Sport, not a championship. */
const NCAA_D1 = [
  ['Baseball', 'Men'], ['Basketball', 'Men'], ['Basketball', 'Women'],
  ['Cross Country', 'Men'], ['Cross Country', 'Women'],
  ['Field Hockey', 'Women'], ['Football (FCS)', 'Men'],
  ['Golf', 'Men'], ['Golf', 'Women'], ['Ice Hockey', 'Men'],
  ['Lacrosse', 'Men'], ['Lacrosse', 'Women'], ['Rowing', 'Women'],
  ['Soccer', 'Men'], ['Soccer', 'Women'], ['Softball', 'Women'],
  ['Swimming & Diving', 'Men'], ['Swimming & Diving', 'Women'],
  ['Tennis', 'Men'], ['Tennis', 'Women'],
  ['Indoor Track & Field', 'Men'], ['Indoor Track & Field', 'Women'],
  ['Outdoor Track & Field', 'Men'], ['Outdoor Track & Field', 'Women'],
  ['Volleyball', 'Women'], ['Wrestling', 'Men'],
]

const NCAA_D2 = [
  ['Baseball', 'Men'], ['Basketball', 'Men'], ['Basketball', 'Women'],
  ['Cross Country', 'Men'], ['Cross Country', 'Women'],
  ['Field Hockey', 'Women'], ['Football', 'Men'],
  ['Golf', 'Men'], ['Golf', 'Women'],
  ['Lacrosse', 'Men'], ['Lacrosse', 'Women'], ['Rowing', 'Women'],
  ['Soccer', 'Men'], ['Soccer', 'Women'], ['Softball', 'Women'],
  ['Swimming & Diving', 'Men'], ['Swimming & Diving', 'Women'],
  ['Tennis', 'Men'], ['Tennis', 'Women'],
  ['Indoor Track & Field', 'Men'], ['Indoor Track & Field', 'Women'],
  ['Outdoor Track & Field', 'Men'], ['Outdoor Track & Field', 'Women'],
  ['Volleyball', 'Women'], ['Wrestling', 'Men'],
]

const NCAA_D3 = [
  ['Baseball', 'Men'], ['Basketball', 'Men'], ['Basketball', 'Women'],
  ['Cross Country', 'Men'], ['Cross Country', 'Women'],
  ['Field Hockey', 'Women'], ['Football', 'Men'],
  ['Golf', 'Men'], ['Golf', 'Women'],
  ['Ice Hockey', 'Men'], ['Ice Hockey', 'Women'],
  ['Lacrosse', 'Men'], ['Lacrosse', 'Women'], ['Rowing', 'Women'],
  ['Soccer', 'Men'], ['Soccer', 'Women'], ['Softball', 'Women'],
  ['Swimming & Diving', 'Men'], ['Swimming & Diving', 'Women'],
  ['Tennis', 'Men'], ['Tennis', 'Women'],
  ['Indoor Track & Field', 'Men'], ['Indoor Track & Field', 'Women'],
  ['Outdoor Track & Field', 'Men'], ['Outdoor Track & Field', 'Women'],
  ['Volleyball', 'Men'], ['Volleyball', 'Women'], ['Wrestling', 'Men'],
]

/* One all-division championship each. Fencing, skiing and rifle award a SINGLE
 * combined team trophy across men's and women's competition, so each appears
 * once as Coed rather than twice — splitting them would invent a title. */
const NCAA_NC = [
  ['Beach Volleyball', 'Women'], ['Bowling', 'Women'], ['Fencing', 'Coed'],
  ['Gymnastics', 'Men'], ['Gymnastics', 'Women'], ['Ice Hockey', 'Women'],
  ['Rifle', 'Coed'], ['Skiing', 'Coed'], ['Volleyball', 'Men'],
  ['Water Polo', 'Men'], ['Water Polo', 'Women'], ['Wrestling', 'Women'],
]

const NCAA_SRC = 'https://www.ncaa.org/championships/'
const ncaa = (list, division) => list.map(([sport, gender]) =>
  C('NCAA', 'National', 'NCAA National Championship', sport, gender, division, NCAA_SRC))

/* ---- CCCAA — California community colleges, 25 state championships -----
 * SR held ZERO. A whole association absent, ~100 member colleges.
 * ⚠️ Conference titles (Big 8, Coast, South Coast…) and the NorCal/SoCal
 * regionals sit BENEATH the state championship and are not recorded here —
 * same rule that keeps CIF sections out of a state count. */
const CCCAA = [
  ['Badminton', 'Women'], ['Baseball', 'Men'], ['Basketball', 'Men'], ['Basketball', 'Women'],
  ['Beach Volleyball', 'Women'], ['Cross Country', 'Men'], ['Cross Country', 'Women'],
  ['Football', 'Men'], ['Golf', 'Men'], ['Golf', 'Women'],
  ['Soccer', 'Men'], ['Soccer', 'Women'], ['Softball', 'Women'],
  ['Swimming & Diving', 'Men'], ['Swimming & Diving', 'Women'],
  ['Tennis', 'Men'], ['Tennis', 'Women'],
  ['Outdoor Track & Field', 'Men'], ['Outdoor Track & Field', 'Women'],
  ['Volleyball', 'Men'], ['Volleyball', 'Women'],
  ['Water Polo', 'Men'], ['Water Polo', 'Women'],
  ['Wrestling', 'Men'], ['Wrestling', 'Women'],
]

/* ---- NWAC — Northwest community colleges, 13 championships -------------
 * SR held ZERO.
 * ⚠️ NWAC TENNIS IS DISCONTINUED — sponsorship ended May 2025, last titles
 * 2024-25. Absent. NWAC sponsors no football and no wrestling; its wrestlers
 * compete at the NJCAA national tournament, which is another body's title. */
const NWAC = [
  ['Baseball', 'Men'], ['Basketball', 'Men'], ['Basketball', 'Women'],
  ['Cross Country', 'Men'], ['Cross Country', 'Women'],
  ['Golf', 'Men'], ['Golf', 'Women'],
  ['Soccer', 'Men'], ['Soccer', 'Women'], ['Softball', 'Women'],
  ['Outdoor Track & Field', 'Men'], ['Outdoor Track & Field', 'Women'],
  ['Volleyball', 'Women'],
]

export const GAPS_COLLEGE = [
  ...ncaa(NCAA_D1, 'Division I'),
  ...ncaa(NCAA_D2, 'Division II'),
  ...ncaa(NCAA_D3, 'Division III'),
  ...ncaa(NCAA_NC, 'National Collegiate'),
  ...CCCAA.map(([sport, gender]) =>
    /* ⚠️ '3C2A', not 'CCCAA'. The association rebranded, and the object already
     * holds 8 records under the new name. Using the legal name would put the
     * same body on the board twice under two brandings — and the first run of
     * the dedupe check duly waved 25 duplicates through, because it read them
     * as different associations. Match the data's convention, not the letterhead. */
    C('3C2A', 'State', 'College', sport, gender, 'State Championship',
      'https://www.cccaasports.org/championships')),
  ...NWAC.map(([sport, gender]) =>
    C('NWAC', 'Conference', 'College', sport, gender, 'NWAC Championship',
      'https://nwacsports.com/championships')),
]
